import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../context/LanguageContext";
import { useContent } from "../../context/ContentContext";
import { api, formatApiErrorDetail } from "../../lib/api";

export default function Estimate() {
  const { t, pick, lang } = useLang();
  const { content } = useContent();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  if (!content) return null;
  const c = content.contact || {};
  const phone = c.phone || "540-314-1957";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(lang === "en" ? "Name and phone are required." : "Nombre y teléfono son obligatorios.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success(t("form_success"));
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("form_error"));
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: Phone, label: t("nav_contact"), value: phone, href: `tel:${phone.replace(/[^0-9]/g, "")}` },
    { icon: Mail, label: "Email", value: c.email, href: c.email ? `mailto:${c.email}` : null },
    { icon: MapPin, label: t("area"), value: pick(c, "area") },
    { icon: Clock, label: t("hours"), value: pick(c, "hours") },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-32 px-5 md:px-10 border-t border-white/10" data-testid="estimate-section">
      <div className="mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-14">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-acid mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-acid" /> {t("estimate_kicker")}
          </p>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] text-white mb-6">
            {t("estimate_title")}
          </h2>
          <p className="text-silver text-base md:text-lg leading-relaxed max-w-md mb-10">
            {t("estimate_sub")}
          </p>

          <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {info.filter((x) => x.value).map((x, i) => {
              const Inner = (
                <div className="bg-obsidian p-6 flex items-start gap-4 h-full">
                  <x.icon className="text-acid shrink-0 mt-1" size={22} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-silver mb-1">{x.label}</p>
                    <p className="text-white font-heading text-lg leading-tight">{x.value}</p>
                  </div>
                </div>
              );
              return x.href ? (
                <a key={i} href={x.href} className="hover:bg-surface transition-colors" data-testid={`contact-info-${i}`}>{Inner}</a>
              ) : (
                <div key={i} data-testid={`contact-info-${i}`}>{Inner}</div>
              );
            })}
          </div>

          <div className="mt-4 border border-white/10 overflow-hidden" data-testid="contact-map">
            <iframe
              title="Zurita's service area map"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(c.map_query || pick(c, "area") || "Winchester, VA")}&z=10&output=embed`}
              className="w-full h-[280px] md:h-[320px] grayscale-[30%] contrast-110"
              style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="border border-white/10 bg-surface p-7 md:p-9 space-y-5"
          data-testid="estimate-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t("form_name")} required>
              <input value={form.name} onChange={set("name")} className={inputCls} data-testid="form-name" placeholder="John Doe" />
            </Field>
            <Field label={t("form_phone")} required>
              <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="form-phone" placeholder="540-000-0000" />
            </Field>
          </div>
          <Field label={t("form_email")}>
            <input value={form.email} onChange={set("email")} className={inputCls} data-testid="form-email" placeholder="you@email.com" />
          </Field>
          <Field label={t("form_service")}>
            <select value={form.service} onChange={set("service")} className={inputCls} data-testid="form-service">
              <option value="">{t("select_service")}</option>
              {(content.services || []).map((s) => (
                <option key={s.id} value={pick(s, "title")}>{pick(s, "title")}</option>
              ))}
            </select>
          </Field>
          <Field label={t("form_message")}>
            <textarea value={form.message} onChange={set("message")} rows={4} className={inputCls} data-testid="form-message" placeholder="..." />
          </Field>
          <button
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 bg-acid py-4 font-heading font-semibold uppercase tracking-wide text-obsidian hover:bg-acid-hover transition-colors disabled:opacity-60"
            data-testid="form-submit"
          >
            <Send size={18} /> {sending ? t("form_sending") : t("form_submit")}
          </button>
        </motion.form>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-obsidian border border-white/15 px-4 py-3 text-white placeholder:text-white/25 outline-none focus:border-acid transition-colors font-sans text-sm";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-silver mb-2 block">
        {label} {required && <span className="text-acid">*</span>}
      </span>
      {children}
    </label>
  );
}
